import React, { useState } from 'react'
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, FormControl, InputLabel, Select, MenuItem, IconButton, Collapse, Chip, Link as MuiLink } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { router, usePage, Link } from '@inertiajs/react'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'

Show.layout = (page) => <AppShell>{page}</AppShell>

const ACCOUNT_TYPE_LABELS = {
  asset: 'Aktiva',
  liability: 'Passiva',
  equity: 'Eigenkapital',
  revenue: 'Erlöse',
  expense: 'Aufwand',
}

const ACCOUNT_TYPE_GROUPS = {
  '1': { label: '1xxx — Finanzkonten', types: ['asset'] },
  '2': { label: '2xxx — Verbindlichkeiten', types: ['liability'] },
  '3': { label: '3xxx — Rücklagen', types: ['equity'] },
  '4': { label: '4xxx — Erlöse', types: ['revenue'] },
  '5': { label: '5xxx — Instandhaltung', types: ['expense'] },
  '6': { label: '6xxx — Verwaltungskosten', types: ['expense'] },
  '7': { label: '7xxx — Betriebskosten', types: ['expense'] },
  '9': { label: '9xxx — Vorträge', types: ['equity'] },
}

export default function Show({ property, chart_of_accounts, fiscal_year, fiscal_years = [], accounts, filter }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}`
  const [expandedGroups, setExpandedGroups] = useState({})
  const [typeFilter, setTypeFilter] = useState(filter || '')

  const onChangeFiscalYear = (id) => {
    router.visit(`${basePath}/chart_of_accounts?fiscal_year_id=${id}`)
  }

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }))
  }

  // Group accounts by first digit
  const parentAccounts = accounts.filter(a => !a.sub_account)
  const subAccounts = accounts.filter(a => a.sub_account)

  const filteredParents = typeFilter
    ? parentAccounts.filter(a => a.account_type === typeFilter)
    : parentAccounts

  const groups = {}
  filteredParents.forEach(a => {
    const key = a.number[0]
    if (!groups[key]) groups[key] = []
    groups[key].push(a)
  })

  return (
    <Box>
      <PageHeader
        title={chart_of_accounts.name}
        breadcrumbs={[
          { label: property.name, href: basePath },
          { label: 'Kontenplan' },
        ]}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        {fiscal_years.length > 0 ? (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Geschäftsjahr</InputLabel>
            <Select
              value={fiscal_year.id}
              label="Geschäftsjahr"
              onChange={(e) => onChangeFiscalYear(e.target.value)}
              data-testid="fiscal-year-filter"
            >
              {fiscal_years.map((fy) => (
                <MenuItem key={fy.id} value={fy.id}>{fy.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Geschäftsjahr: {fiscal_year.start_date} – {fiscal_year.end_date}
          </Typography>
        )}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Kontenart</InputLabel>
          <Select value={typeFilter} label="Kontenart" onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value="">Alle</MenuItem>
            <MenuItem value="asset">Aktiva</MenuItem>
            <MenuItem value="liability">Passiva</MenuItem>
            <MenuItem value="equity">Eigenkapital</MenuItem>
            <MenuItem value="revenue">Erlöse</MenuItem>
            <MenuItem value="expense">Aufwand</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([key, groupAccounts]) => (
        <Paper key={key} elevation={0} sx={{ mb: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, cursor: 'pointer', '&:hover': { backgroundColor: 'background.subtle' } }}
            onClick={() => toggleGroup(key)}
          >
            <IconButton size="small">
              {expandedGroups[key] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
            <Typography variant="subtitle2" sx={{ ml: 1 }}>
              {ACCOUNT_TYPE_GROUPS[key]?.label || `${key}xxx`}
            </Typography>
            <Chip label={`${groupAccounts.length} Konten`} size="small" sx={{ ml: 1 }} />
          </Box>
          <Collapse in={expandedGroups[key]}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                    <TableCell sx={{ fontWeight: 600, width: 120 }}>Konto</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Bezeichnung</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 100 }}>Typ</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 100 }}>PK-Typ</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 120 }}>Kostenkategorie</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupAccounts.map(account => {
                    const children = subAccounts.filter(s => s.parent_id === account.id)
                    const accountUrl = `${basePath}/ledger_accounts/${account.id}?fiscal_year_id=${fiscal_year.id}`
                    return (
                      <React.Fragment key={account.id}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            <MuiLink component={Link} href={accountUrl} underline="hover" color="inherit">
                              {account.number}
                            </MuiLink>
                          </TableCell>
                          <TableCell>
                            <MuiLink component={Link} href={accountUrl} underline="hover" color="inherit">
                              {account.name}
                            </MuiLink>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{ACCOUNT_TYPE_LABELS[account.account_type]}</Typography>
                          </TableCell>
                          <TableCell>
                            {account.personenkonto_type !== 'none' && (
                              <Chip label={account.personenkonto_type} size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">{account.cost_category_name}</Typography>
                          </TableCell>
                        </TableRow>
                        {children.map(sub => {
                          const subUrl = `${basePath}/ledger_accounts/${sub.id}?fiscal_year_id=${fiscal_year.id}`
                          return (
                          <TableRow key={sub.id} sx={{ backgroundColor: 'background.subtle' }}>
                            <TableCell sx={{ pl: 4, color: 'text.secondary' }}>
                              <MuiLink component={Link} href={subUrl} underline="hover" color="inherit">
                                {sub.number}
                              </MuiLink>
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              <MuiLink component={Link} href={subUrl} underline="hover" color="inherit">
                                {sub.name}
                              </MuiLink>
                            </TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell />
                          </TableRow>
                          )
                        })}
                      </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Paper>
      ))}
    </Box>
  )
}
